import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styles from "./VerifierDashboard.module.css";
import VerifierApi from "../../utils/api/VerifierApi";
import Auth from "../../utils/api/Auth";
import { userDataActions } from "../../store/redux-store/Index";
import Pagination from "../../components/list/Pagination";
import { toClientPaginatedResult } from "../../utils/pagination";

const ORDERS_PER_PAGE = 1;
const NIID_EXPIRY_DATE_PATTERN = /^(?:[1-9]|[12]\d|3[01])\s(?:JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)\s\d{4}$/;

const getOrders = (response) => {
  const payload = response?.data?.data ?? response?.data;
  return Array.isArray(payload) ? payload : [];
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

function VerifierDashboard() {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.userData.loginData.token);
  const user = useSelector((state) => state.userData.personalInfo);
  const loginData = useSelector((state) => state.userData.loginData);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeOrderId, setActiveOrderId] = useState(null);
  const [expiryDates, setExpiryDates] = useState({});
  const [savingOrderId, setSavingOrderId] = useState(null);
  const [reporting, setReporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [copiedOrderIds, setCopiedOrderIds] = useState({});
  const [detailSlideIndexes, setDetailSlideIndexes] = useState({});

  const loadOrders = useCallback(async () => {
    try {
      const response = await VerifierApi.fetchManualOrders({ token });
      setOrders(getOrders(response));
      setCurrentPage(1);
      setCopiedOrderIds({});
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to load renewal orders."), {
        position: "top-left",
        theme: "dark",
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadOrders();
  };

  const handleUpdate = (order) => {
    setActiveOrderId(order.id);
    setExpiryDates((current) => ({
      ...current,
      [order.id]: current[order.id] || order.niid_expiry_date || "",
    }));
  };

  const handleCopyPlate = async (order) => {
    const plateNumber = order.plate_no;

    if (!plateNumber) {
      toast.error("This order does not have a plate number to copy.", {
        position: "top-left",
        theme: "dark",
      });
      return;
    }

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(plateNumber);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = plateNumber;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        const copied = document.execCommand("copy");
        document.body.removeChild(textarea);
        if (!copied) throw new Error("Copy was not available");
      }

      setCopiedOrderIds((current) => ({ ...current, [order.id]: true }));
    } catch (error) {
      toast.error("Unable to copy the plate number. Please try again.", {
        position: "top-left",
        theme: "dark",
      });
    }
  };

  const handleExpiryPaste = (event, order) => {
    event.preventDefault();

    if (!copiedOrderIds[order.id]) {
      toast.warn(
        "Please ensure you copy the plate number before pasting the NIID response.",
        { position: "top-left", theme: "dark" },
      );
      return;
    }

    const pastedDate = event.clipboardData
      .getData("text")
      .trim()
      .replace(/\s+/g, " ")
      .toUpperCase();

    if (!NIID_EXPIRY_DATE_PATTERN.test(pastedDate)) {
      toast.error("Use the NIID expiry format 8 AUG 2026.", {
        position: "top-left",
        theme: "dark",
      });
      return;
    }

    setExpiryDates((current) => ({ ...current, [order.id]: pastedDate }));
  };

  const handlePasteExpiryFromClipboard = async (order) => {
    if (!copiedOrderIds[order.id]) {
      toast.warn(
        "Please ensure you copy the plate number before pasting the NIID response.",
        { position: "top-left", theme: "dark" },
      );
      return;
    }

    if (!navigator.clipboard?.readText || !window.isSecureContext) {
      toast.error(
        "Clipboard access is unavailable here. Copy the NIID date and paste it into the field.",
        { position: "top-left", theme: "dark" },
      );
      return;
    }

    try {
      const pastedDate = (await navigator.clipboard.readText())
        .trim()
        .replace(/\s+/g, " ")
        .toUpperCase();

      if (!pastedDate) {
        toast.error("Your clipboard does not contain an NIID expiry date.", {
          position: "top-left",
          theme: "dark",
        });
        return;
      }

      if (!NIID_EXPIRY_DATE_PATTERN.test(pastedDate)) {
        toast.error("Use the NIID expiry format 8 AUG 2026.", {
          position: "top-left",
          theme: "dark",
        });
        return;
      }

      setExpiryDates((current) => ({ ...current, [order.id]: pastedDate }));
    } catch (error) {
      toast.error(
        "Unable to read your clipboard. Copy the NIID date and paste it into the field.",
        { position: "top-left", theme: "dark" },
      );
    }
  };

  const setDetailSlide = (orderId, nextIndex, slideCount) => {
    setDetailSlideIndexes((current) => ({
      ...current,
      [orderId]: Math.max(0, Math.min(nextIndex, slideCount - 1)),
    }));
  };

  const clearExpiryDate = (orderId) => {
    setExpiryDates((current) => ({ ...current, [orderId]: "" }));
  };

  const handleSaveExpiry = async (order) => {
    const niidExpiryDate = expiryDates[order.id];

    if (!niidExpiryDate) {
      toast.error("Paste an NIID expiry date before saving.", {
        position: "top-left",
        theme: "dark",
      });
      return;
    }

    if (!copiedOrderIds[order.id]) {
      toast.warn(
        "Please ensure you copy the plate number before pasting the NIID response.",
        { position: "top-left", theme: "dark" },
      );
      return;
    }

    if (!NIID_EXPIRY_DATE_PATTERN.test(niidExpiryDate)) {
      toast.error("Use the NIID expiry format 8 AUG 2026.", {
        position: "top-left",
        theme: "dark",
      });
      return;
    }

    try {
      setSavingOrderId(order.id);
      await VerifierApi.setNiidExpiryDate({
        token,
        uniqueId: order.unique_id,
        niidExpiryDate,
      });
      setOrders((current) =>
        current.map((item) =>
          item.id === order.id
            ? {
                ...item,
                is_niid_active: 1,
                niid_expiry_date: niidExpiryDate,
              }
            : item,
        ),
      );
      setActiveOrderId(null);
      toast.success(`${order.plate_no || "Order"} marked as NIID active.`, {
        position: "top-left",
        theme: "dark",
      });
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to update the NIID expiry date."), {
        position: "top-left",
        theme: "dark",
      });
    } finally {
      setSavingOrderId(null);
    }
  };

  const handleSendReport = async () => {
    try {
      setReporting(true);
      await VerifierApi.sendNiidReport({ token });
      toast.success("NIID report submitted successfully.", {
        position: "top-left",
        theme: "dark",
      });
      handleRefresh();
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to submit the NIID report."), {
        position: "top-left",
        theme: "dark",
      });
    } finally {
      setReporting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await Auth.logoutHandler({ token });
    } catch (error) {
      // Clear the local session even when the remote logout call cannot finish.
    } finally {
      dispatch(userDataActions.logout());
    }
  };

  const displayName = user?.fullname || loginData?.fullname || "Verifier";
  const activeCount = orders.filter((order) => Number(order.is_niid_active) === 1).length;
  const paginatedOrders = toClientPaginatedResult({
    items: orders,
    page: currentPage,
    perPage: ORDERS_PER_PAGE,
  });

  return (
    <main className={styles.page}>
      <ToastContainer />
      <header className={styles.header}>
        <div>
          <p className={styles.eyebrow}>NIID verification</p>
          <h1>Renewal Orders</h1>
          <p className={styles.welcome}>Signed in as {displayName}</p>
        </div>
        <button className={styles.logoutButton} onClick={handleLogout} type="button">
          Logout
        </button>
      </header>

      <section className={styles.summary} aria-label="Order summary">
        <div>
          <span>Orders loaded</span>
          <strong>{orders.length}</strong>
        </div>
        <div>
          <span>NIID active</span>
          <strong>{activeCount}</strong>
        </div>
      </section>

      <section className={styles.content}>
        <div className={styles.sectionHeader}>
          <div>
            <h2>Renewal order details</h2>
            <p>Update each order with its NIID expiry date before sending the report.</p>
          </div>
          <button
            className={styles.refreshButton}
            onClick={handleRefresh}
            disabled={loading || refreshing}
            type="button"
          >
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        {loading && <div className={styles.loading}>Loading renewal orders...</div>}

        {!loading && orders.length === 0 && (
          <div className={styles.emptyState}>
            <h3>No orders to verify</h3>
            <p>There are no manual renewal orders available at the moment.</p>
          </div>
        )}

        {!loading && orders.length > 0 && (
          <>
          <div className={styles.orderCarousel}>
            {paginatedOrders.items.map((order) => {
              const vehicle = order.order_details?.[0]?.plate_details || order.plate_details || {};
              const isActive = Number(order.is_niid_active) === 1;
              const isUpdating = activeOrderId === order.id;
              const isSaving = savingOrderId === order.id;
              const detailSlides = [
                { label: "Vehicle", value: [vehicle.make, vehicle.model].filter(Boolean).join(" ") || "Not available" },
                { label: "Agent", value: order.agent_details?.fullname || "Unassigned agent" },
                { label: "Current expiry", value: order.niid_expiry_date || "Not set" },
                { label: "Order ID", value: order.unique_id || "Not available" },
              ];
              const activeDetailIndex = detailSlideIndexes[order.id] || 0;
              const activeDetail = detailSlides[activeDetailIndex];
              const expiryDate = expiryDates[order.id] || "";
              const hasValidExpiryDate = NIID_EXPIRY_DATE_PATTERN.test(expiryDate);

              return (
                <article className={styles.orderCard} key={order.id || order.unique_id}>
                  <div className={styles.cardHeader}>
                    <div>
                      <button
                        className={styles.copyPlateButton}
                        onClick={() => handleCopyPlate(order)}
                        type="button"
                        aria-label="Copy vehicle plate number"
                        title="Copy vehicle plate number"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M16 1H4C2.9 1 2 1.9 2 3v14h2V3h12V1zm3 4H8C6.9 5 6 5.9 6 7v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" fill="currentColor" />
                        </svg>
                      </button>
                      {/* <p className={styles.orderId}>Copy the vehicle plate before pasting the NIID response.</p> */}
                    </div>
                    <span className={`${styles.status} ${isActive ? styles.active : styles.pending}`}>
                      {isActive ? "NIID active" : "Pending"}
                    </span>
                  </div>

                  <section className={styles.detailCarousel} aria-label={`Details for ${order.plate_no || "this order"}`}>
                    <div className={styles.carouselViewport} aria-live="polite">
                      <p className={styles.carouselLabel}>{activeDetail.label}</p>
                      <p className={styles.carouselValue}>{activeDetail.value}</p>
                    </div>
                    <div className={styles.carouselControls}>
                      <button
                        type="button"
                        onClick={() => setDetailSlide(order.id, activeDetailIndex - 1, detailSlides.length)}
                        disabled={activeDetailIndex === 0}
                        aria-label="Previous detail"
                      >
                        Previous
                      </button>
                      <span>{activeDetailIndex + 1} / {detailSlides.length}</span>
                      <button
                        type="button"
                        onClick={() => setDetailSlide(order.id, activeDetailIndex + 1, detailSlides.length)}
                        disabled={activeDetailIndex === detailSlides.length - 1}
                        aria-label="Next detail"
                      >
                        Next
                      </button>
                    </div>
                  </section>

                  <dl className={styles.details} aria-hidden="true">
                    <div><dt>Vehicle</dt><dd>{[vehicle.make, vehicle.model].filter(Boolean).join(" ") || "—"}</dd></div>
                    <div><dt>Customer</dt><dd>{vehicle.fullname || "—"}</dd></div>
                    <div><dt>Order status</dt><dd>{order.order_details?.[0]?.order_status || "—"}</dd></div>
                    <div><dt>Current expiry</dt><dd>{order.niid_expiry_date || "Not set"}</dd></div>
                  </dl>

                  {isUpdating ? (
                    <div className={styles.updateForm}>
                      <label htmlFor={`expiry-${order.id}`}>NIID expiry date</label>
                      <p className={styles.pasteHint}>Copy this vehicle's plate number, then paste a date in the format 8 AUG 2026.</p>
                      <div className={styles.updateControls}>
                        <div className={styles.expiryInputWrapper}>
                          <input
                            id={`expiry-${order.id}`}
                            type="text"
                            value={expiryDate}
                            placeholder="8 AUG 2026"
                            readOnly
                            inputMode="none"
                            onPaste={(event) => handleExpiryPaste(event, order)}
                            disabled={isSaving}
                          />
                          {hasValidExpiryDate && (
                            <button
                              className={styles.clearExpiryButton}
                              onClick={() => clearExpiryDate(order.id)}
                              disabled={isSaving}
                              type="button"
                              aria-label="Clear expiry date"
                              title="Clear expiry date"
                            >
                              ×
                            </button>
                          )}
                        </div>
                        <button
                          className={styles.updateActionButton}
                          onClick={() =>
                            hasValidExpiryDate
                              ? handleSaveExpiry(order)
                              : handlePasteExpiryFromClipboard(order)
                          }
                          disabled={isSaving}
                          type="button"
                        >
                          {isSaving ? "Saving..." : hasValidExpiryDate ? "Save" : "Paste"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button className={styles.updateButton} onClick={() => handleUpdate(order)} type="button">
                      Update
                    </button>
                  )}
                </article>
              );
            })}
          </div>
          <Pagination
            pagination={paginatedOrders.pagination}
            onPageChange={setCurrentPage}
            isLoading={loading || refreshing}
          />
          </>
        )}
      </section>

      <button
        className={styles.reportButton}
        onClick={handleSendReport}
        disabled={loading || orders.length === 0 || reporting}
        type="button"
      >
        {reporting ? "Submitting NIID report..." : "Submit NIID report"}
      </button>
    </main>
  );
}

export default VerifierDashboard;
